import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Settings, LogOut, 
  Menu, X, ChevronRight, Video, ShieldCheck, Activity, BarChart3, Inbox, UserCheck, MonitorPlay, BookOpen
} from 'lucide-react';
import { RTBLogo } from '@/components/RTBLogo';

export const AdminLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('lbu_admin_token');
    if (!token) {
      navigate('/admin');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('lbu_admin_token');
    navigate('/admin');
  };

  const adminRole = (() => {
    try {
      const token = localStorage.getItem('lbu_admin_token');
      if (!token) return 'admin';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.admin_role || 'admin';
    } catch { return 'admin'; }
  })();

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/participants', label: 'Participants', icon: Users },
    { path: '/admin/leads', label: 'Leads', icon: Activity },
    { path: '/admin/videos', label: 'AI Videos', icon: Video },
    { path: '/admin/email-analytics', label: 'Email Tracking', icon: BarChart3 },
    { path: '/admin/inbox', label: 'Inbox', icon: Inbox },
    { path: '/admin/background-checks', label: 'BG Checks', icon: UserCheck },
    { path: '/admin/meetings', label: 'Meetings', icon: MonitorPlay },
    { path: '/admin/features', label: 'Features', icon: BookOpen },
    ...(adminRole === 'super_admin' ? [{ path: '/admin/team', label: 'Team', icon: ShieldCheck }] : []),
    { path: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-obsidian text-white/90" data-testid="admin-layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col fixed top-0 left-0 bottom-0 w-64 bg-charcoal border-r border-white/[0.06] z-30">
        <div className="p-6 border-b border-white/[0.06]">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <RTBLogo dark className="h-8" />
          </Link>
        </div>

        <nav className="py-4 flex-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                isActive(item.path)
                  ? 'text-rtb-purple bg-rtb-purple/10 border-r-2 border-rtb-purple'
                  : 'text-white/40 hover:text-white/80 hover:bg-white/[0.03]'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/[0.06]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-6 py-3 text-sm w-full text-red-400 hover:text-red-300 hover:bg-red-500/5 transition-colors rounded-lg"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-charcoal border-b border-white/[0.06] px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <RTBLogo dark className="h-6" />
            <span className="font-oswald font-bold text-sm text-white/60">ADMIN</span>
          </Link>
          <button onClick={() => setSidebarOpen(true)} className="text-white/60">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}>
          <div className="fixed top-0 right-0 bottom-0 w-64 bg-charcoal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
              <span className="font-oswald font-bold text-white/80">Menu</span>
              <button onClick={() => setSidebarOpen(false)} className="text-white/40">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="py-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                    isActive(item.path)
                      ? 'text-rtb-purple bg-rtb-purple/10'
                      : 'text-white/40 hover:text-white/80'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-6 py-3 text-sm w-full text-red-400"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="border-b border-white/[0.06] px-6 py-4">
          <div className="flex items-center gap-2 text-white/30 text-sm mb-1">
            <Link to="/admin/dashboard" className="hover:text-white/60 transition-colors">Admin</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white/70">{title}</span>
          </div>
          <h1 className="text-2xl font-oswald font-bold text-white">{title}</h1>
        </div>
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};
